import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Handshake, FileText, CalendarDays, Telescope, MessageCircle, ShoppingBag, GraduationCap } from 'lucide-react';
import { DashboardProfileBanner } from '../components/ProfileNudgeProvider';
import ClientEngagementTicker from '../components/ClientEngagementTicker';
import FeaturedMaterialHero from '../components/FeaturedMaterialHero';

import AwardGlassdoor from '../assets/awards/9.png';
import AwardForbes from '../assets/awards/10.png';
import AwardOktaPartnerAmerSI from '../assets/awards/11.png';
import AwardOktaChampionTech from '../assets/awards/12.png';
import AwardOktaTechnicalChampionAPJ from '../assets/awards/13.png';
import AwardOktaPartnerAmer2024 from '../assets/awards/13_in.png';
import AwardOktaCertified from '../assets/awards/14.png';
import AwardTechBridgePanel from '../assets/awards/15.png';
import AwardTechBridgeLogo from '../assets/awards/16.png';
import AwardOktaApex from '../assets/awards/17.png';

const awards = [
  { src: AwardGlassdoor,               alt: 'Glassdoor 5/5 rating' },
  { src: AwardOktaApex,                 alt: 'Okta Elevate Partner Program — Apex Tier' },
  { src: AwardOktaPartnerAmer2024,      alt: 'Okta Partner of the Year 2024 — AMER' },
  { src: AwardOktaPartnerAmerSI,        alt: 'Okta Partner of the Year 2023 — AMER Public Sector SI' },
  { src: AwardOktaChampionTech,         alt: 'Okta Champion of the Year 2023 — Technology Partner' },
  { src: AwardOktaTechnicalChampionAPJ, alt: 'Okta Technical Champion 2023 — APJ' },
  { src: AwardOktaCertified,            alt: 'Okta Certified Partner' },
  { src: AwardForbes,                   alt: 'Forbes Business Council, official member 2023' },
  { src: AwardTechBridgePanel,          alt: 'TechBridge 2023 — CxO Panel Specialist on IAM' },
  { src: AwardTechBridgeLogo,           alt: 'TechBridge' },
];

const journeys = [
  { path: '/engage',       Icon: Handshake,     label: 'Engage RAAH on a Deal',  desc: 'Request pre-sales, in-flight, or post-sales support' },
  { path: '/materials',    Icon: FileText,       label: 'Get / Request Materials', desc: 'Search or request decks, case studies, and assets' },
  { path: '/booking',      Icon: CalendarDays,   label: 'Book Time with RAAH',     desc: 'Schedule time with CIAM or Workforce teams' },
  { path: '/capabilities', Icon: Telescope,      label: 'Explore Capabilities',    desc: 'Learn what RAAH offers and how we deliver' },
  { path: '/ask',          Icon: MessageCircle,  label: 'Ask a Question',          desc: 'Get answers via AI-assisted chatbot (Copilot)' },
  { path: '/marketplace',  Icon: ShoppingBag,    label: 'Browse Marketplace',      desc: 'Explore BOLT, WATT, and AMP' },
  { path: '/workshops',    Icon: GraduationCap,  label: 'Workshops & Events',      desc: 'Browse AI, Workforce, and Auth0 workshops' },
];

const styles = {
  greeting: { fontSize: '20px', fontWeight: '600', marginBottom: '4px' },
  sub:      { color: '#666', fontSize: '13px', marginBottom: '20px' },
  grid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' },
  card:     { background: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '20px', cursor: 'pointer', transition: 'border-color 0.15s' },
  iconWrap: { width: '40px', height: '40px', borderRadius: '10px', background: '#fef0e6', color: '#fd5f0d', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' },
  label:    { fontSize: '14px', fontWeight: '600', marginBottom: '4px' },
  desc:     { fontSize: '12px', color: '#777' },
  awardsCard:     { background: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '20px', marginTop: '24px' },
  awardsTitle:    { fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '16px' },
  awardsViewport: {
    overflow: 'hidden', width: '100%',
    WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 48px, #000 calc(100% - 48px), transparent 100%)',
    maskImage: 'linear-gradient(90deg, transparent 0, #000 48px, #000 calc(100% - 48px), transparent 100%)',
  },
  awardImg: { height: '84px', width: 'auto', objectFit: 'contain', flex: 'none', display: 'block' },
  // Rendered via a portal into document.body (see below) — an ordinary
  // absolutely-positioned child here would get clipped by awardsViewport's
  // overflow:hidden, which is required for the horizontal marquee clipping.
  awardTooltip: (rect) => ({
    position: 'fixed', top: rect.bottom + 10, left: rect.left + rect.width / 2, transform: 'translateX(-50%)',
    background: '#1a1a1a', color: '#fff', fontSize: '12px', fontWeight: '500',
    padding: '7px 11px', borderRadius: '6px', whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)', zIndex: 9999, pointerEvents: 'none',
  }),
};

// A plain CSS animation can't be expressed via inline style objects, so the
// marquee keyframes/hover-pause/reduced-motion rules live in one injected
// <style> tag instead of introducing a separate CSS file for this one section.
const marqueeCSS = `
  .PortalAwardsTrack {
    display: flex;
    align-items: center;
    gap: 48px;
    width: max-content;
    animation: PortalAwardsScroll 32s linear infinite;
  }
  .PortalAwardsTrack:hover {
    animation-play-state: paused;
  }
  @keyframes PortalAwardsScroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .PortalAwardsTrack { animation: none; }
  }
`;

// Auth0 commonly defaults the `name` claim to the account's email address for
// plain email/password signups that never set a separate display name - so
// "prefer user.name" alone still ends up greeting people by email. Treat name
// claims that are just the email again (or look like an email) as unavailable,
// and only fall back to the real email as a last resort.
function getGreetingName(user) {
  const looksLikeEmail = (v) => typeof v === 'string' && v.includes('@');
  if (user?.name && !looksLikeEmail(user.name)) return user.name;
  if (user?.given_name) return user.given_name;
  if (user?.nickname && !looksLikeEmail(user.nickname)) return user.nickname;
  return user?.email || '';
}

function Dashboard() {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const [hoveredAward, setHoveredAward] = useState(null); // { alt, rect }
  const greetingName = getGreetingName(user);

  return (
    <div>
      <div style={styles.greeting}>
        Welcome back{greetingName ? `, ${greetingName}` : ''}
      </div>
      <div style={styles.sub}>What are you trying to do today?</div>

      <FeaturedMaterialHero />

      <div style={{ marginBottom: '24px' }}>
        <ClientEngagementTicker />
      </div>

      {/* Profile completion banner */}
      <DashboardProfileBanner />

      <div style={styles.grid}>
        {journeys.map(({ path, Icon, label, desc }) => (
          <div
            key={path}
            style={styles.card}
            onClick={() => navigate(path)}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#fd5f0d'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e5e5'}
          >
            <div style={styles.iconWrap}><Icon size={20} strokeWidth={1.75} /></div>
            <div style={styles.label}>{label}</div>
            <div style={styles.desc}>{desc}</div>
          </div>
        ))}
      </div>

      <div style={styles.awardsCard}>
        <style>{marqueeCSS}</style>
        <div style={styles.awardsTitle}>Awards & Recognition</div>
        <div style={styles.awardsViewport}>
          <div className="PortalAwardsTrack">
            {awards.map(({ src, alt }) => (
              <img
                key={alt}
                src={src}
                alt={alt}
                style={styles.awardImg}
                onMouseEnter={e => setHoveredAward({ alt, rect: e.currentTarget.getBoundingClientRect() })}
                onMouseLeave={() => setHoveredAward(null)}
              />
            ))}
            {/* Duplicate, hidden from assistive tech — makes the scroll loop seamless */}
            {awards.map(({ src, alt }) => (
              <img key={`${alt}-dup`} src={src} alt="" aria-hidden="true" style={styles.awardImg} />
            ))}
          </div>
        </div>
      </div>

      {hoveredAward && createPortal(
        <div style={styles.awardTooltip(hoveredAward.rect)}>{hoveredAward.alt}</div>,
        document.body
      )}
    </div>
  );
}

export default Dashboard;